import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, StyleSheet, ScrollView, TouchableOpacity } from 'react-native';
import { useLocalSearchParams, Stack, useRouter } from 'expo-router';
import { Ionicons } from '@expo/vector-icons';
import { api, apiGet, apiPost, ApiError } from '../../../src/api/client';
import { useAuth } from '../../../src/context/AuthContext';
import { useToast } from '../../../src/components/Toast';
import Button from '../../../src/components/Button';
import Card from '../../../src/components/Card';
import CollapsibleSection from '../../../src/components/CollapsibleSection';
import PageFooter from '../../../src/components/PageFooter';
import ReportProblemModal from '../../../src/components/ReportProblemModal';
import { useTheme } from '../../../src/context/ThemeContext';
import { confirmChoice } from '../../../src/utils/confirm';
import { bundleItemsSummary } from '../../../src/utils/bundleItems';
import { vendingCreditLabel } from '../../../src/utils/planBenefits';
import { spacing, fontSize, fontWeight, borderRadius } from '../../../src/utils/theme';
import { rs } from '../../../src/utils/responsive';
import type { ColorTheme } from '../../../src/utils/theme';

interface VendingMachine {
  id: string;
  locationId: string;
  name: string;
  machineType: string;
  controllerId: string | null;
  relayIndex: number;
  creditStepCents: number;
  presets: number[] | null;
  maxCreditCents: number;
  isActive: boolean;
}

interface Location {
  id: string;
  ownerId: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zip: string;
}

/** locationScope-aware "purchasable here" check for plan/bundle offers. */
function offerCoversLocation(scope: string, offerLocationName: string | null, loc: Location): boolean {
  return scope === 'all' || !offerLocationName || offerLocationName === loc.name;
}

interface VendMembership {
  hasCredit: boolean;
  planName?: string;
  remainingCents?: number;
  capCents?: number;
  usedCents?: number;
}

interface VendBundle {
  purchasedBundleId: string;
  planName: string;
  remainingCreditCents: number;
  expiresAt: string | null;
}

/**
 * Vending-scoped voucher from GET /api/me/vouchers?machineId= (server-filtered
 * to vouchers with appliesTo.vending valid at this machine's location).
 * free_vend covers up to freeVendCents of the credit; percent discounts.
 */
interface VendVoucher {
  id: string;
  code: string;
  discountKind: 'percent' | 'free_vend' | 'free_wash' | 'free_minutes';
  percentOff: number | null;
  freeVendCents: number | null;
  appliesToLabel?: string | null;
  expiresAt: string | null;
  ownerName: string;
}

/** Headline for a vending voucher choice/banner. */
function vendVoucherTitle(v: VendVoucher): string {
  if (v.discountKind === 'free_vend') return `$${((v.freeVendCents ?? 0) / 100).toFixed(2)} free vending credit`;
  return `${v.percentOff}% off vending credit`;
}

// Subscription plan with a monthly vending-credit bucket, purchasable here
// (server-filtered by ?machineId=; absent endpoint → section hidden).
interface AvailableVendPlan {
  id: string;
  name: string;
  description: string | null;
  priceMonthly: number;
  vendingCreditCentsPerMonth?: number | null;
  locationScope: string;
  locationName: string | null;
  ownerBusinessName: string;
}

// Bundle plan containing a vending_credit item, purchasable here.
interface AvailableVendBundle {
  id: string;
  name: string;
  description: string | null;
  bundlePrice: number;
  items?: {
    kind: 'wash' | 'vac_minutes' | 'vending_credit';
    tierName: string | null;
    quantity: number | null;
    minutes: number | null;
    creditCents: number | null;
    allAtOnce?: boolean;
  }[];
  expirationDays: number | null;
  expirationMinutes?: number | null;
  ownerBusinessName: string;
}

// Client-generated idempotency token for bundle purchases (same pattern as
// the wash screen): retrying the same confirm with the same token returns
// the original purchase instead of charging twice.
function makePurchaseToken(): string {
  const g = globalThis as any;
  if (g.crypto?.randomUUID) return g.crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, ch => {
    const r = (Math.random() * 16) | 0;
    const v = ch === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/**
 * CreditIncrementPicker — the membership/bundle spend amount chooser. Shows
 * the SAME configured credit amounts as the card box, disabling any above
 * the remaining benefit, plus a "Use remaining" chip whenever the remaining
 * balance isn't itself a configured option (lets customers drain the last
 * cents). Module-level on purpose — never define components inline.
 */
interface CreditIncrementPickerProps {
  readonly options: number[];
  readonly remainingCents: number;
  readonly value: number | null;
  readonly onChange: (amountCents: number) => void;
  readonly colors: ColorTheme;
  readonly styles: ReturnType<typeof useStyles>;
}

function CreditIncrementPicker({ options, remainingCents, value, onChange, colors, styles }: CreditIncrementPickerProps) {
  const showRemaining = remainingCents > 0 && !options.includes(remainingCents);
  return (
    <View style={styles.presetGrid}>
      {options.map(amount => {
        const disabled = amount > remainingCents;
        const selected = value === amount;
        return (
          <TouchableOpacity
            key={amount}
            style={[styles.incBtn, selected && styles.presetBtnSelected, disabled && styles.incBtnDisabled]}
            disabled={disabled}
            onPress={() => onChange(amount)}
            accessibilityRole="radio"
            accessibilityState={{ selected, checked: selected, disabled }}
            accessibilityLabel={`$${(amount / 100).toFixed(2)} credit`}
          >
            <Text style={[styles.incPrice, selected && styles.presetPriceSelected, disabled && styles.incPriceDisabled]}>
              ${(amount / 100).toFixed(2)}
            </Text>
          </TouchableOpacity>
        );
      })}
      {showRemaining && (
        <TouchableOpacity
          style={[styles.incBtn, value === remainingCents && styles.presetBtnSelected]}
          onPress={() => onChange(remainingCents)}
          accessibilityRole="radio"
          accessibilityState={{ selected: value === remainingCents, checked: value === remainingCents }}
          accessibilityLabel={`Use remaining $${(remainingCents / 100).toFixed(2)}`}
        >
          <Text style={[styles.incPrice, value === remainingCents && styles.presetPriceSelected]}>
            ${(remainingCents / 100).toFixed(2)}
          </Text>
          <Text style={[styles.incRemainingLabel, value === remainingCents && { color: colors.primary }]}>use remaining</Text>
        </TouchableOpacity>
      )}
    </View>
  );
}

/** VendScreen - vending machine credit purchase (card, membership, or bundle). */
export default function VendScreen() {
  const { colors } = useTheme();
  const styles = useStyles(colors);
  const { locationId, machineId } = useLocalSearchParams<{ locationId: string; machineId: string }>();
  const [machine, setMachine] = useState<VendingMachine | null>(null);
  const [location, setLocation] = useState<Location | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadFailed, setLoadFailed] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null);
  const [purchasing, setPurchasing] = useState(false);
  const [errorBanner, setErrorBanner] = useState<string | null>(null);
  const [membership, setMembership] = useState<VendMembership | null>(null);
  const [vendBundles, setVendBundles] = useState<VendBundle[]>([]);
  // Per-box increment picks (wave 2): membership + each bundle choose their
  // own amount from the machine's configured options, capped by remaining.
  const [membershipAmount, setMembershipAmount] = useState<number | null>(null);
  const [bundleAmounts, setBundleAmounts] = useState<Record<string, number | null>>({});
  // Purchasable plans/bundles with vending benefits (collapsed offer boxes).
  const [availableVendPlans, setAvailableVendPlans] = useState<AvailableVendPlan[]>([]);
  const [availableVendBundles, setAvailableVendBundles] = useState<AvailableVendBundle[]>([]);
  const [subscribingPlan, setSubscribingPlan] = useState<string | null>(null);
  const [purchasingBundlePlan, setPurchasingBundlePlan] = useState<string | null>(null);
  const [washCreditCents, setWashCreditCents] = useState<number | null>(null);
  const [useWashCredit, setUseWashCredit] = useState(false);
  // Vouchers are opt-in, never auto-applied (same rule as the wash screen).
  const [availableVouchers, setAvailableVouchers] = useState<VendVoucher[]>([]);
  const [appliedVoucher, setAppliedVoucher] = useState<VendVoucher | null>(null);
  // Success: credit was injected — customer makes their selection at the machine.
  const [vendSuccess, setVendSuccess] = useState<{
    amountCents: number;
    method: 'card' | 'membership' | 'bundle';
    transactionId: string;
    creditAppliedCents?: number;
    voucherDiscountCents?: number;
    voucherKind?: 'percent' | 'free_vend';
    voucherPercentOff?: number | null;
    chargedCents?: number;
  } | null>(null);
  const [showReport, setShowReport] = useState(false);

  const { isAuthenticated, isLoading: authLoading, user } = useAuth();
  const router = useRouter();
  const toast = useToast();

  // Auth guard — machine details require auth, so redirect first.
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      router.replace(`/login?returnTo=/vend/${locationId}/${machineId}`);
    }
  }, [authLoading, isAuthenticated]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    const ac = new AbortController();
    (async () => {
      try {
        const [m, loc] = await Promise.all([
          api<VendingMachine>(`/api/vending-machines/${machineId}`),
          api<Location>(`/api/locations/${locationId}`),
        ]);
        if (ac.signal.aborted) return;
        setMachine(m);
        setLocation(loc);
        // Best-effort extras: membership credit, bundle credit, gift credit.
        try {
          const mem = await apiGet<VendMembership>(`/api/vending-machines/${machineId}/membership`);
          if (!ac.signal.aborted) setMembership(mem);
        } catch { /* silent */ }
        if (ac.signal.aborted) return;
        try {
          const bc = await apiGet<{ hasBundles: boolean; bundles: VendBundle[] }>(`/api/bundles/check?machineId=${machineId}`);
          if (!ac.signal.aborted) setVendBundles(bc.bundles ?? []);
        } catch { /* silent */ }
        if (ac.signal.aborted) return;
        try {
          const res = await api<{ credits: { ownerId: string; balanceCents: number }[] }>('/api/me/wash-credit');
          if (ac.signal.aborted) return;
          const match = res.credits.find(c => c.ownerId === loc.ownerId);
          setWashCreditCents(match ? match.balanceCents : 0);
          if (match && match.balanceCents > 0) setUseWashCredit(true);
        } catch { /* silent */ }
        if (ac.signal.aborted) return;
        // Vending-scoped vouchers valid at this machine (server-filtered).
        try {
          const vres = await apiGet<{ vouchers: VendVoucher[] }>(`/api/me/vouchers?machineId=${encodeURIComponent(machineId)}`);
          if (!ac.signal.aborted) setAvailableVouchers(vres.vouchers ?? []);
        } catch { /* silent */ }
        if (ac.signal.aborted) return;
        // Purchasable membership plans with vending credit. The /available
        // endpoints filter by owner (not machine), so we filter client-side
        // to plans that include vending credit and cover this location.
        try {
          const plans = await apiGet<(AvailableVendPlan & { locationScope: string; locationName: string | null })[]>(
            `/api/subscriptions/available?ownerId=${encodeURIComponent(loc.ownerId)}`);
          if (!ac.signal.aborted) {
            setAvailableVendPlans((plans ?? []).filter(p =>
              (p.vendingCreditCentsPerMonth ?? 0) > 0 && offerCoversLocation(p.locationScope, p.locationName, loc)));
          }
        } catch { /* silent */ }
        if (ac.signal.aborted) return;
        // Purchasable bundle plans containing vending credit.
        try {
          const abs = await apiGet<(AvailableVendBundle & { locationScope?: string; locationName?: string | null })[]>(
            `/api/bundles/available?ownerId=${encodeURIComponent(loc.ownerId)}`);
          if (!ac.signal.aborted) {
            setAvailableVendBundles((abs ?? []).filter(b =>
              b.items?.some(i => i.kind === 'vending_credit')
              && offerCoversLocation(b.locationScope ?? 'all', b.locationName ?? null, loc)));
          }
        } catch { /* silent */ }
      } catch {
        if (ac.signal.aborted) return;
        setLoadFailed(true);
        toast.show({ type: 'error', title: 'Error', message: 'Could not load vending machine' });
      } finally {
        if (!ac.signal.aborted) setLoading(false);
      }
    })();
    return () => ac.abort();
  }, [machineId, locationId, authLoading, isAuthenticated]);

  // Credit amount choices: machine presets when configured, else $1/$2/$3
  // (only amounts that are valid step multiples and within the max).
  const amountOptions = useMemo(() => {
    if (!machine) return [];
    const step = machine.creditStepCents || 25;
    if (machine.presets?.length) {
      return machine.presets.filter(p => p <= machine.maxCreditCents && p % step === 0);
    }
    const defaults = [100, 200, 300].filter(a => a <= machine.maxCreditCents && a % step === 0);
    if (defaults.length > 0) return defaults;
    const fallback: number[] = [];
    for (let a = step; a <= machine.maxCreditCents && fallback.length < 3; a += step) fallback.push(a);
    return fallback;
  }, [machine]);

  useEffect(() => {
    if (selectedAmount == null && amountOptions.length > 0) setSelectedAmount(amountOptions[0]);
  }, [amountOptions, selectedAmount]);

  // Default the membership picker to the first configured amount that fits
  // the remaining benefit, else the exact remainder (drain).
  useEffect(() => {
    if (!membership?.hasCredit || membershipAmount != null) return;
    const rem = membership.remainingCents ?? 0;
    if (rem <= 0) return;
    const fit = amountOptions.find(a => a <= rem);
    setMembershipAmount(fit ?? rem);
  }, [membership, amountOptions, membershipAmount]);

  // Same defaulting per bundle row.
  useEffect(() => {
    if (vendBundles.length === 0) return;
    setBundleAmounts(prev => {
      const next = { ...prev };
      let changed = false;
      for (const b of vendBundles) {
        const rem = b.remainingCreditCents;
        const cur = next[b.purchasedBundleId];
        if (cur == null || cur > rem) {
          const fit = amountOptions.find(a => a <= rem);
          next[b.purchasedBundleId] = fit ?? (rem > 0 ? rem : null);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [vendBundles, amountOptions]);

  const refreshMembership = () => {
    apiGet<VendMembership>(`/api/vending-machines/${machineId}/membership`)
      .then(setMembership)
      .catch(() => {});
  };

  const ensurePaymentMethod = async (): Promise<boolean> => {
    try {
      const res = await api<{ hasPaymentMethods: boolean }>('/api/payments/has-methods');
      if (!res.hasPaymentMethods) {
        toast.show({
          type: 'warning',
          title: 'Payment Method Required',
          message: 'Please add a card before buying vending credit.',
          duration: 0,
          actions: [
            { label: 'Cancel', onPress: () => toast.hide(), style: 'cancel' },
            { label: 'Add Card', onPress: () => { toast.hide(); router.push(`/payment-methods?returnTo=/vend/${locationId}/${machineId}`); } },
          ],
        });
        return false;
      }
      return true;
    } catch {
      return true; // backend will catch it
    }
  };

  const redirectIfVerifyRequired = (err: any): boolean => {
    if (err instanceof ApiError && err.code === 'EMAIL_VERIFICATION_REQUIRED') {
      router.push({
        pathname: '/verify-email',
        params: { email: user?.email ?? '', returnTo: `/vend/${locationId}/${machineId}` },
      } as never);
      return true;
    }
    return false;
  };

  // Client-side mirror of the backend's voucher math (free_vend covers up to
  // freeVendCents; percent discounts) — for card-gating + button labels only;
  // the server recomputes authoritatively.
  const voucherDiscountFor = (amountCents: number): number => {
    if (!appliedVoucher) return 0;
    if (appliedVoucher.discountKind === 'free_vend') {
      return Math.min(amountCents, appliedVoucher.freeVendCents ?? 0);
    }
    if (appliedVoucher.discountKind === 'percent') {
      return Math.round(amountCents * Math.min(100, Math.max(0, appliedVoucher.percentOff ?? 0)) / 100);
    }
    return 0;
  };

  // Card pay (voucher discounts first, then wash credit auto-draws server-side;
  // only an explicit credit opt-out is sent).
  const handleCardPurchase = async () => {
    if (!machine || selectedAmount == null) return;
    const afterVoucher = Math.max(0, selectedAmount - voucherDiscountFor(selectedAmount));
    const voucherCoversAll = !!appliedVoucher && afterVoucher === 0;
    const creditCoversAll = useWashCredit && washCreditCents != null && washCreditCents >= afterVoucher;
    if (!voucherCoversAll && !creditCoversAll && !(await ensurePaymentMethod())) return;
    setPurchasing(true);
    setErrorBanner(null);
    try {
      const body: any = { machineId: machine.id, amountCents: selectedAmount };
      if (appliedVoucher) body.discountSource = { type: 'voucher', code: appliedVoucher.code };
      if ((washCreditCents ?? 0) > 0 && !useWashCredit) body.useWashCredit = false;
      const res = await api<{
        transactionId: string; amount: number; creditCents: number;
        creditAppliedCents: number; voucherDiscountCents?: number; pointsEarned: number; status: string;
      }>('/api/purchases/vending', { method: 'POST', body });
      setVendSuccess({
        amountCents: res.creditCents,
        method: 'card',
        transactionId: res.transactionId,
        creditAppliedCents: res.creditAppliedCents || undefined,
        voucherDiscountCents: res.voucherDiscountCents || undefined,
        voucherKind: appliedVoucher?.discountKind === 'free_vend' ? 'free_vend'
          : appliedVoucher?.discountKind === 'percent' ? 'percent' : undefined,
        voucherPercentOff: appliedVoucher?.percentOff ?? null,
        chargedCents: res.amount,
      });
      setAppliedVoucher(null);
      setAvailableVouchers(prev => prev.filter(v => v.code !== appliedVoucher?.code));
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      if (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') {
        router.push(`/payment-methods?returnTo=/vend/${locationId}/${machineId}`);
        return;
      }
      // VOUCHER_* codes (expired / exhausted / wrong scope / claimed by
      // another account …): show the server's message inline and drop the
      // dead voucher so a retry goes through without it.
      if (err instanceof ApiError && typeof err.code === 'string' && err.code.startsWith('VOUCHER_')) {
        setErrorBanner(err.message || 'That voucher can’t be used here.');
        if (appliedVoucher) {
          setAvailableVouchers(prev => prev.filter(v => v.code !== appliedVoucher.code));
          setAppliedVoucher(null);
        }
        return;
      }
      setErrorBanner(err.message || 'Purchase failed. You were not charged.');
    } finally {
      setPurchasing(false);
    }
  };

  // Spend the membership's monthly vending-credit bucket at the amount the
  // customer picked in the membership box (any configured option ≤ remaining,
  // or the exact remainder).
  const handleMembershipSpend = async () => {
    if (!machine || membershipAmount == null) return;
    setPurchasing(true);
    setErrorBanner(null);
    try {
      const res = await apiPost<{ transactionId: string }>(
        '/api/subscriptions/authorize-vending',
        { machineId: machine.id, amountCents: membershipAmount },
      );
      setVendSuccess({ amountCents: membershipAmount, method: 'membership', transactionId: res.transactionId });
      setMembershipAmount(null); // re-default against the new remainder
      refreshMembership();
    } catch (err: any) {
      if (err instanceof ApiError && err.code === 'SUBSCRIPTION_LIMIT') {
        toast.show({ type: 'info', title: 'Monthly vending credit used up', message: 'You can pay by card below.' });
        refreshMembership();
        return;
      }
      setErrorBanner(err.message || 'Could not use membership credit.');
    } finally {
      setPurchasing(false);
    }
  };

  // Spend prepaid bundle vending credit at the amount picked in that
  // bundle's box.
  const handleBundleSpend = async (b: VendBundle) => {
    const amt = bundleAmounts[b.purchasedBundleId];
    if (!machine || amt == null) return;
    setPurchasing(true);
    setErrorBanner(null);
    try {
      const res = await apiPost<{ transactionId: string; creditCents: number; remainingCents: number }>(
        '/api/bundles/authorize-vending',
        { purchasedBundleId: b.purchasedBundleId, machineId: machine.id, amountCents: amt },
      );
      setVendSuccess({ amountCents: res.creditCents ?? amt, method: 'bundle', transactionId: res.transactionId });
      setBundleAmounts(prev => ({ ...prev, [b.purchasedBundleId]: null }));
      setVendBundles(prev => prev
        .map(x => x.purchasedBundleId === b.purchasedBundleId
          ? { ...x, remainingCreditCents: res.remainingCents }
          : x)
        .filter(x => x.remainingCreditCents > 0));
    } catch (err: any) {
      setErrorBanner(err.message || 'Could not use bundle credit.');
    } finally {
      setPurchasing(false);
    }
  };

  // ── Purchase-offer handlers (collapsed boxes) ──

  const handleSubscribeVendPlan = async (plan: AvailableVendPlan) => {
    if (!(await ensurePaymentMethod())) return;
    const creditLine = vendingCreditLabel(plan.vendingCreditCentsPerMonth);
    confirmChoice(
      `Start your ${plan.name} membership?`,
      `You'll be charged $${(plan.priceMonthly / 100).toFixed(2)}/mo, renewing monthly${creditLine ? ` — includes ${creditLine.toLowerCase()}` : ''}. Cancel anytime from your account.`,
      () => performSubscribeVendPlan(plan.id),
      'Yes, start membership',
    );
  };

  const performSubscribeVendPlan = async (planId: string) => {
    setSubscribingPlan(planId);
    setErrorBanner(null);
    try {
      await apiPost('/api/subscriptions/subscribe', { planId });
      toast.show({ type: 'success', title: 'Membership active!', message: 'Your monthly vending credit is ready below.' });
      setAvailableVendPlans([]);
      refreshMembership();
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      if (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') {
        router.push(`/payment-methods?returnTo=/vend/${locationId}/${machineId}`);
        return;
      }
      setErrorBanner(err.message || 'Could not start the membership. You were not charged.');
    } finally {
      setSubscribingPlan(null);
    }
  };

  const handleBuyVendBundle = async (ab: AvailableVendBundle) => {
    if (!(await ensurePaymentMethod())) return;
    const contents = bundleItemsSummary(ab.items) ?? 'Prepaid vending credit';
    const expiryNote = ab.expirationMinutes
      ? `, valid for ${ab.expirationMinutes} minutes after purchase`
      : ab.expirationDays ? `, valid for ${ab.expirationDays} days` : '';
    const purchaseToken = makePurchaseToken();
    confirmChoice(
      `Get the ${ab.name}?`,
      `${contents} for $${(ab.bundlePrice / 100).toFixed(2)} — yours to use anytime${expiryNote}.`,
      () => performBuyVendBundle(ab.id, purchaseToken),
      'Yes, buy bundle',
    );
  };

  const performBuyVendBundle = async (bundlePlanId: string, purchaseToken: string, confirmDuplicate = false) => {
    setPurchasingBundlePlan(bundlePlanId);
    setErrorBanner(null);
    try {
      await apiPost('/api/bundles/purchase', {
        bundlePlanId,
        purchaseToken,
        ...(confirmDuplicate ? { confirmDuplicate: true } : {}),
      });
      toast.show({ type: 'success', title: 'Purchase complete!', message: 'Your prepaid bundle credit is ready below.' });
      try {
        const bc = await apiGet<{ hasBundles: boolean; bundles: VendBundle[] }>(`/api/bundles/check?machineId=${machineId}`);
        setVendBundles(bc.bundles ?? []);
      } catch { /* ok */ }
    } catch (err: any) {
      if (redirectIfVerifyRequired(err)) return;
      if (err instanceof ApiError && err.code === 'DUPLICATE_RECENT') {
        confirmChoice(
          'Buy this bundle again?',
          'You bought this same bundle just minutes ago — it may already be in your bundle credit above. Do you want to buy another one?',
          () => performBuyVendBundle(bundlePlanId, makePurchaseToken(), true),
          'Yes, buy another',
        );
        return;
      }
      if (err instanceof ApiError && err.code === 'NO_PAYMENT_METHOD') {
        router.push(`/payment-methods?returnTo=/vend/${locationId}/${machineId}`);
        return;
      }
      setErrorBanner(err.message || 'Purchase failed. You were not charged.');
    } finally {
      setPurchasingBundlePlan(null);
    }
  };

  if (loading || authLoading) {
    return (
      <View style={styles.loadingContainer}>
        <Stack.Screen options={{ title: 'Loading...' }} />
        <Ionicons name="fast-food" size={48} color={colors.primary} />
        <Text style={styles.loadingText}>Loading vending machine...</Text>
      </View>
    );
  }

  if (loadFailed || !machine || !location) {
    return (
      <>
        <Stack.Screen options={{ title: 'Unavailable' }} />
        <View style={styles.inactiveContainer}>
          <Ionicons name="fast-food-outline" size={64} color={colors.textLight} />
          <Text style={styles.inactiveTitle}>Machine Unavailable</Text>
          <Text style={styles.inactiveSubtext}>This vending machine could not be loaded. Please try again.</Text>
          <TouchableOpacity style={styles.inactiveBtn} onPress={() => router.replace('/')} hitSlop={8}>
            <Ionicons name="home" size={22} color={colors.primary} />
            <Text style={styles.inactiveBtnText}>Home</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  if (!machine.isActive) {
    return (
      <>
        <Stack.Screen options={{ title: location.name }} />
        <View style={styles.inactiveContainer}>
          <Ionicons name="fast-food-outline" size={64} color={colors.textLight} />
          <Text style={styles.inactiveTitle}>Machine Turned Off</Text>
          <Text style={styles.inactiveSubtext}>{machine.name} at {location.name} is not accepting app payments right now.</Text>
          <TouchableOpacity style={styles.inactiveBtn} onPress={() => router.replace('/')} hitSlop={8}>
            <Ionicons name="home" size={22} color={colors.primary} />
            <Text style={styles.inactiveBtnText}>Home</Text>
          </TouchableOpacity>
        </View>
      </>
    );
  }

  // Success — the pulses were sent while the customer stands at the machine.
  if (vendSuccess) {
    return (
      <>
        <Stack.Screen options={{ title: 'Credit Sent' }} />
        <ScrollView style={{ flex: 1, backgroundColor: colors.background }} contentContainerStyle={{ flexGrow: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.lg }}>
          <View style={styles.successIconRing}>
            <Ionicons name="checkmark-circle" size={72} color={colors.success} />
          </View>
          <Text style={styles.successHeadline}>Credit Sent to the Machine</Text>
          <Text style={styles.successSub}>Make your selection at the machine now.</Text>
          <View style={styles.successDetailBadge}>
            <Ionicons name="fast-food" size={16} color={colors.primary} />
            <Text style={styles.successDetailText}>
              ${(vendSuccess.amountCents / 100).toFixed(2)} credit
              {vendSuccess.method === 'membership' ? ' · Membership' : vendSuccess.method === 'bundle' ? ' · Bundle' : ''}
            </Text>
          </View>
          {vendSuccess.voucherDiscountCents ? (
            <View style={[styles.successDetailBadge, { backgroundColor: colors.success + '15' }]}>
              <Ionicons name="gift" size={16} color={colors.success} />
              <Text style={[styles.successDetailText, { color: colors.success }]}>
                {vendSuccess.voucherKind === 'free_vend'
                  ? `$${(vendSuccess.voucherDiscountCents / 100).toFixed(2)} free credit`
                  : `${vendSuccess.voucherPercentOff}% off — saved $${(vendSuccess.voucherDiscountCents / 100).toFixed(2)}`}
              </Text>
            </View>
          ) : null}
          <View style={styles.washInfo}>
            <Text style={styles.washInfoText}>{location.name}</Text>
            <Text style={styles.washInfoText}>{machine.name}</Text>
          </View>
          <Button
            title="Done"
            size="lg"
            style={{ width: '100%', maxWidth: rs(360), marginTop: spacing.lg }}
            onPress={() => { setVendSuccess(null); router.replace('/'); }}
          />
          <TouchableOpacity onPress={() => setShowReport(true)} hitSlop={8} style={{ marginTop: spacing.md }}>
            <Text style={{ color: colors.textSecondary, fontSize: fontSize.sm }}>Item didn't vend? Report a problem</Text>
          </TouchableOpacity>
        </ScrollView>
        <ReportProblemModal
          visible={showReport}
          onClose={() => setShowReport(false)}
          transactionId={vendSuccess.transactionId}
          locationName={location.name}
          bayName={machine.name}
          amount={vendSuccess.chargedCents ?? vendSuccess.amountCents}
          type="vending"
        />
      </>
    );
  }

  return (
    <>
      <Stack.Screen options={{ title: location.name }} />
      <ScrollView style={styles.container} contentContainerStyle={styles.content}>
        {errorBanner && (
          <TouchableOpacity onPress={() => setErrorBanner(null)}>
            <View style={styles.errorBanner}>
              <Ionicons name="alert-circle" size={20} color={colors.danger} />
              <Text style={styles.errorBannerText}>{errorBanner}</Text>
              <Ionicons name="close" size={16} color={colors.textLight} />
            </View>
          </TouchableOpacity>
        )}

        <View style={styles.locationBox}>
          <View style={styles.locationBoxHeader}>
            <Ionicons name="fast-food" size={24} color={colors.white} />
            <Text style={styles.locationBoxName}>{location.name}</Text>
          </View>
          <Text style={styles.locationBoxAddress}>
            {location.address}, {location.city}, {location.state} {location.zip}
          </Text>
        </View>

        <Card style={styles.machineCard}>
          <View style={styles.machineHeader}>
            <Ionicons name="fast-food" size={20} color={colors.textSecondary} />
            <Text style={styles.machineName}>{machine.name}</Text>
            <View style={styles.machineTypeBadge}>
              <Text style={styles.machineTypeText}>Vending</Text>
            </View>
          </View>
        </Card>

        {/* ── Hierarchy (wave 2): membership → bundles → pay by card ── */}

        {/* Membership vending credit — spend at any configured increment. */}
        {membership?.hasCredit && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.xs }}>
              <Ionicons name="ribbon" size={22} color={colors.primary} />
              <Text style={styles.selectorTitle}>Membership Credit</Text>
            </View>
            <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.sm }}>
              {membership.planName} — ${((membership.remainingCents ?? 0) / 100).toFixed(2)} of ${((membership.capCents ?? 0) / 100).toFixed(2)} left this month
            </Text>
            {(membership.remainingCents ?? 0) > 0 ? (
              <>
                <CreditIncrementPicker
                  options={amountOptions}
                  remainingCents={membership.remainingCents ?? 0}
                  value={membershipAmount}
                  onChange={setMembershipAmount}
                  colors={colors}
                  styles={styles}
                />
                <Button
                  title={membershipAmount == null
                    ? 'Select an Amount'
                    : `Use Membership — $${(membershipAmount / 100).toFixed(2)} credit`}
                  onPress={handleMembershipSpend}
                  loading={purchasing}
                  disabled={membershipAmount == null}
                  size="lg"
                  icon={<Ionicons name="ribbon" size={20} color={colors.white} />}
                />
              </>
            ) : (
              <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary }}>
                Monthly credit used up — pay by card below.
              </Text>
            )}
          </Card>
        )}

        {/* Collapsed membership purchase offer — plans with vending credit. */}
        {!membership?.hasCredit && availableVendPlans.length > 0 && (
          <CollapsibleSection
            title="Membership Plans"
            subtitle="Monthly plans with vending credit — tap to view"
            icon="ribbon-outline"
            iconColor={colors.primary}
            style={[styles.selectorCard, { borderColor: colors.primary + '40', borderWidth: 1 }]}
          >
            {availableVendPlans.map(plan => (
              <View key={plan.id} style={styles.offerRow}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.text }}>{plan.name}</Text>
                  <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.primary }}>
                    ${(plan.priceMonthly / 100).toFixed(2)}<Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.normal, color: colors.textSecondary }}>/mo</Text>
                  </Text>
                </View>
                {plan.description && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs }}>{plan.description}</Text>
                )}
                {vendingCreditLabel(plan.vendingCreditCentsPerMonth) && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4, marginBottom: spacing.sm }}>
                    <Ionicons name="fast-food-outline" size={13} color={colors.primary} />
                    <Text style={{ fontSize: fontSize.xs, color: colors.primary, fontWeight: fontWeight.medium }}>
                      {vendingCreditLabel(plan.vendingCreditCentsPerMonth)}
                    </Text>
                  </View>
                )}
                <Button
                  title={subscribingPlan === plan.id ? 'Subscribing...' : `Subscribe — $${(plan.priceMonthly / 100).toFixed(2)}/mo`}
                  onPress={() => handleSubscribeVendPlan(plan)}
                  loading={subscribingPlan === plan.id}
                  disabled={!!subscribingPlan}
                  size="sm"
                />
              </View>
            ))}
          </CollapsibleSection>
        )}

        {/* Prepaid bundle vending credit — spend at any configured increment. */}
        {vendBundles.length > 0 && (
          <Card style={[styles.selectorCard, { borderColor: colors.success + '60', borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm }}>
              <Ionicons name="cube" size={22} color={colors.success} />
              <Text style={styles.selectorTitle}>Bundle Credit</Text>
            </View>
            {vendBundles.map(b => {
              const amt = bundleAmounts[b.purchasedBundleId] ?? null;
              return (
                <View key={b.purchasedBundleId} style={{ marginBottom: spacing.sm }}>
                  <Text style={{ fontSize: fontSize.sm, color: colors.textSecondary, marginBottom: spacing.xs }}>
                    {b.planName} — ${(b.remainingCreditCents / 100).toFixed(2)} left
                    {b.expiresAt ? ` · expires ${new Date(b.expiresAt).toLocaleDateString()}` : ''}
                  </Text>
                  <CreditIncrementPicker
                    options={amountOptions}
                    remainingCents={b.remainingCreditCents}
                    value={amt}
                    onChange={(v) => setBundleAmounts(prev => ({ ...prev, [b.purchasedBundleId]: v }))}
                    colors={colors}
                    styles={styles}
                  />
                  <Button
                    title={amt == null
                      ? 'Select an Amount'
                      : `Use Bundle — $${(amt / 100).toFixed(2)} credit`}
                    onPress={() => handleBundleSpend(b)}
                    loading={purchasing}
                    disabled={amt == null}
                    size="lg"
                    icon={<Ionicons name="cube" size={20} color={colors.white} />}
                  />
                </View>
              );
            })}
          </Card>
        )}

        {/* Collapsed bundle purchase offer — bundle plans with vending credit. */}
        {availableVendBundles.length > 0 && (
          <CollapsibleSection
            title={vendBundles.length > 0 ? 'Buy More Bundles' : 'Bundles'}
            subtitle="Prepaid packs with vending credit — tap to view"
            icon="cube-outline"
            iconColor={colors.success}
            style={[styles.selectorCard, { borderColor: colors.success + '40', borderWidth: 1 }]}
          >
            {availableVendBundles.map(ab => (
              <View key={ab.id} style={styles.offerRow}>
                <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginBottom: spacing.xs }}>
                  <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.text }}>{ab.name}</Text>
                  <Text style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.primary }}>
                    ${(ab.bundlePrice / 100).toFixed(2)}
                  </Text>
                </View>
                {ab.description && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.textSecondary, marginBottom: spacing.xs }}>{ab.description}</Text>
                )}
                {bundleItemsSummary(ab.items) && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.textLight, marginBottom: spacing.sm }}>
                    {bundleItemsSummary(ab.items)}
                  </Text>
                )}
                <Button
                  title={purchasingBundlePlan === ab.id ? 'Purchasing...' : `Buy Bundle — $${(ab.bundlePrice / 100).toFixed(2)}`}
                  onPress={() => handleBuyVendBundle(ab)}
                  loading={purchasingBundlePlan === ab.id}
                  disabled={!!purchasingBundlePlan}
                  size="sm"
                />
              </View>
            ))}
          </CollapsibleSection>
        )}

        {/* Gift credit — auto-draws server-side; toggle sends an explicit opt-out. */}
        {washCreditCents !== null && washCreditCents > 0 && (
          <Card style={[styles.selectorCard, useWashCredit && { borderColor: colors.primary, borderWidth: 2 }]}>
            <TouchableOpacity onPress={() => setUseWashCredit(!useWashCredit)} style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name={useWashCredit ? 'checkbox' : 'square-outline'} size={28} color={useWashCredit ? colors.primary : colors.textLight} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.selectorTitle, { marginBottom: 2 }]}>
                  Gift credit: ${(washCreditCents / 100).toFixed(2)}
                </Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
                  {useWashCredit
                    ? 'Pays for this credit first. Anything it doesn’t cover goes to your card.'
                    : 'Not being used — tap to pay with your credit.'}
                </Text>
              </View>
            </TouchableOpacity>
          </Card>
        )}

        {/* Applied voucher banner — shows what's active + a Remove button */}
        {appliedVoucher && (
          <Card style={[styles.selectorCard, { borderColor: colors.primary, borderWidth: 2 }]}>
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
              <Ionicons name="gift" size={28} color={colors.primary} />
              <View style={{ flex: 1 }}>
                <Text style={[styles.selectorTitle, { marginBottom: 2 }]}>{vendVoucherTitle(appliedVoucher)} applied</Text>
                <Text style={{ color: colors.textSecondary, fontSize: 13 }}>
                  From {appliedVoucher.ownerName} · 1 use
                </Text>
              </View>
              <TouchableOpacity onPress={() => setAppliedVoucher(null)} hitSlop={8}>
                <Text style={{ color: colors.primary, fontSize: 14, fontWeight: fontWeight.semibold }}>Remove</Text>
              </TouchableOpacity>
            </View>
          </Card>
        )}

        {/* Available vouchers — opt-in chooser (never auto-applied). */}
        {!appliedVoucher && availableVouchers.length > 0 && (
          <Card style={styles.selectorCard}>
            <Text style={styles.selectorTitle}>You have a promo available</Text>
            {availableVouchers.map(v => (
              <TouchableOpacity
                key={v.code}
                onPress={() => setAppliedVoucher(v)}
                style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingVertical: 8, borderBottomWidth: 1, borderBottomColor: colors.borderLight }}
              >
                <Ionicons name="gift-outline" size={22} color={colors.primary} />
                <View style={{ flex: 1 }}>
                  <Text style={{ fontSize: 15, fontWeight: fontWeight.semibold, color: colors.text }}>
                    {vendVoucherTitle(v)}
                  </Text>
                  <Text style={{ color: colors.textSecondary, fontSize: 12, marginTop: 2 }}>
                    From {v.ownerName} · 1 use
                  </Text>
                </View>
                <Text style={{ color: colors.primary, fontWeight: fontWeight.semibold }}>Apply</Text>
              </TouchableOpacity>
            ))}
          </Card>
        )}

        {/* Credit amount picker */}
        <Card style={styles.selectorCard}>
          <Text style={styles.selectorTitle}>Select Credit Amount</Text>
          <Text style={styles.helpText}>
            Credit is sent to the machine like coins — pick your item at the machine after paying.
          </Text>
          <View style={styles.presetGrid}>
            {amountOptions.map(amount => (
              <TouchableOpacity
                key={amount}
                style={[styles.presetBtn, selectedAmount === amount && styles.presetBtnSelected]}
                onPress={() => setSelectedAmount(amount)}
                accessibilityRole="radio"
                accessibilityState={{ selected: selectedAmount === amount, checked: selectedAmount === amount }}
                accessibilityLabel={`$${(amount / 100).toFixed(2)} vending credit`}
              >
                <Text style={[styles.presetPrice, selectedAmount === amount && styles.presetPriceSelected]}>
                  ${(amount / 100).toFixed(2)}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
          <Button
            title={selectedAmount == null
              ? 'Select an Amount'
              : (() => {
                  const promo = voucherDiscountFor(selectedAmount);
                  const due = Math.max(0, selectedAmount - promo);
                  if (promo <= 0) return `Pay $${(selectedAmount / 100).toFixed(2)}`;
                  if (due === 0) return `Get $${(selectedAmount / 100).toFixed(2)} Credit — Free (promo)`;
                  return `Pay $${(due / 100).toFixed(2)} (promo −$${(promo / 100).toFixed(2)})`;
                })()}
            onPress={handleCardPurchase}
            loading={purchasing}
            disabled={selectedAmount == null}
            size="lg"
            style={styles.payBtn}
            icon={<Ionicons name="card" size={20} color={colors.white} />}
          />
        </Card>

        <View style={styles.secureRow}>
          <Ionicons name="lock-closed" size={14} color={colors.textLight} />
          <Text style={styles.secureText}>Payments secured by Stripe</Text>
        </View>
        <PageFooter />
      </ScrollView>
    </>
  );
}

const createStyles = (c: ColorTheme) => StyleSheet.create({
  container: { flex: 1, backgroundColor: c.background },
  content: { padding: spacing.md, paddingBottom: spacing.xxl },
  loadingContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', backgroundColor: c.background },
  loadingText: { fontSize: fontSize.md, color: c.textSecondary, marginTop: spacing.md },

  errorBanner: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.sm,
    padding: spacing.md, borderRadius: borderRadius.md, marginBottom: spacing.md,
    backgroundColor: c.danger + '10', borderWidth: 1, borderColor: c.danger + '30',
  },
  errorBannerText: { flex: 1, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: c.danger },

  locationBox: { backgroundColor: '#733e24', borderRadius: borderRadius.lg, padding: spacing.lg, marginBottom: spacing.md },
  locationBoxHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginBottom: spacing.sm },
  locationBoxName: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.white, flex: 1 },
  locationBoxAddress: { fontSize: fontSize.sm, color: 'rgba(255,255,255,0.85)' },

  machineCard: { marginBottom: spacing.md },
  machineHeader: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm },
  machineName: { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: c.text, flex: 1 },
  machineTypeBadge: { backgroundColor: c.primaryBg, paddingHorizontal: spacing.sm, paddingVertical: spacing.xs, borderRadius: borderRadius.full },
  machineTypeText: { fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: c.primaryDark },

  selectorCard: { marginBottom: spacing.md },
  selectorTitle: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.text, marginBottom: spacing.md },
  helpText: { fontSize: fontSize.sm, color: c.textSecondary, marginTop: -spacing.sm, marginBottom: spacing.sm },
  presetGrid: { flexDirection: 'row', flexWrap: 'wrap', gap: spacing.sm, marginTop: spacing.sm, marginBottom: spacing.md, justifyContent: 'center' },
  presetBtn: { minWidth: rs(90), paddingVertical: spacing.md, paddingHorizontal: spacing.lg, borderRadius: borderRadius.lg, borderWidth: 2, borderColor: c.border, alignItems: 'center', backgroundColor: c.surface },
  presetBtnSelected: { borderColor: c.primary, backgroundColor: c.primaryBg },
  presetPrice: { fontSize: fontSize.xl, fontWeight: fontWeight.bold, color: c.text },
  presetPriceSelected: { color: c.primary },
  payBtn: { shadowColor: c.primary, shadowOffset: { width: 0, height: 4 }, shadowOpacity: 0.2, shadowRadius: 8, elevation: 4 },

  // Membership/bundle increment picker (wave 2) — compact chips
  incBtn: {
    minWidth: rs(76), paddingVertical: spacing.sm, paddingHorizontal: spacing.md,
    borderRadius: borderRadius.lg, borderWidth: 2, borderColor: c.border,
    alignItems: 'center', backgroundColor: c.surface,
  },
  incBtnDisabled: { opacity: 0.4 },
  incPrice: { fontSize: fontSize.md, fontWeight: fontWeight.bold, color: c.text },
  incPriceDisabled: { color: c.textLight },
  incRemainingLabel: { fontSize: fontSize.xs, color: c.textLight, marginTop: 1 },

  // Purchase-offer rows inside the collapsed sections
  offerRow: { padding: spacing.md, backgroundColor: c.background, borderRadius: borderRadius.md, marginBottom: spacing.sm },

  washInfo: { marginTop: spacing.md, gap: 2, alignItems: 'center' },
  washInfoText: { fontSize: fontSize.sm, color: c.textSecondary },

  successIconRing: {
    width: rs(104), height: rs(104), borderRadius: rs(52),
    backgroundColor: c.success + '15',
    justifyContent: 'center', alignItems: 'center',
    marginBottom: spacing.md,
  },
  successHeadline: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, textAlign: 'center' },
  successSub: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', marginTop: spacing.xs },
  successDetailBadge: {
    flexDirection: 'row', alignItems: 'center', gap: spacing.xs,
    backgroundColor: c.primary + '10',
    paddingHorizontal: spacing.md, paddingVertical: spacing.sm,
    borderRadius: borderRadius.full, marginTop: spacing.md,
  },
  successDetailText: { fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: c.primary },

  inactiveContainer: { flex: 1, justifyContent: 'center', alignItems: 'center', padding: spacing.xl, backgroundColor: c.background },
  inactiveTitle: { fontSize: fontSize.xxl, fontWeight: fontWeight.bold, color: c.text, marginTop: spacing.lg, marginBottom: spacing.sm },
  inactiveSubtext: { fontSize: fontSize.md, color: c.textSecondary, textAlign: 'center', maxWidth: rs(300), lineHeight: 22 },
  inactiveBtn: { flexDirection: 'row', alignItems: 'center', gap: spacing.sm, marginTop: spacing.xl, paddingVertical: spacing.md, paddingHorizontal: spacing.xl, borderRadius: borderRadius.md, borderWidth: 1.5, borderColor: c.primary },
  inactiveBtnText: { fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: c.primaryDark },

  secureRow: { flexDirection: 'row', alignItems: 'center', justifyContent: 'center', gap: spacing.xs, marginTop: spacing.md },
  secureText: { fontSize: fontSize.xs, color: c.textLight },
});
const useStyles = (c: ColorTheme) => React.useMemo(() => createStyles(c), [c]);
